import { useState, type FormEvent } from "react";
import { useGetList, useTranslate, type Identifier } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { AppRole, Sale } from "../types";

const MAX_ADVISOR_CHOICES = 200;
// The reassignment RPCs only accept an asesora or a lider as the new owner.
const OWNER_ROLES: readonly AppRole[] = ["lider", "asesora"];

const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-base";

export type ReassignSubmit = (params: {
  newSalesId: Identifier;
  advisorName: string;
  reason: string;
}) => Promise<string | null>;

/**
 * Almikia Epic EL3: documented reassignment. An advisor picker plus a MANDATORY written reason
 * (submit stays disabled while it is blank). `onSubmit` resolves to null on success or to the
 * error message, which is shown inline; the server enforces who may reassign.
 */
export const ReassignDialog = ({
  open,
  onClose,
  onSubmit,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: ReassignSubmit;
  saving: boolean;
}) => {
  const translate = useTranslate();
  const [salesId, setSalesId] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: MAX_ADVISOR_CHOICES },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: open },
  );
  const advisors = (sales ?? []).filter(
    (sale) => !sale.disabled && OWNER_ROLES.includes(sale.role),
  );

  const reasonMissing = reason.trim() === "";
  const canSubmit = !reasonMissing && salesId !== "" && !saving;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    const advisor = advisors.find((sale) => String(sale.id) === salesId);
    const message = await onSubmit({
      newSalesId: advisor?.id ?? salesId,
      advisorName: advisor ? `${advisor.first_name} ${advisor.last_name}` : "",
      reason: reason.trim(),
    });
    if (message === null) {
      setReason("");
      setSalesId("");
      setTouched(false);
      setError(null);
      onClose();
    } else {
      setError(message || translate("crm.reassign.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{translate("crm.reassign.title")}</DialogTitle>
            <DialogDescription className="sr-only">
              {translate("crm.reassign.reason_label")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reassign-advisor">
              {translate("crm.reassign.advisor_label")}
            </Label>
            <select
              id="reassign-advisor"
              className={SELECT_CLASS}
              value={salesId}
              onChange={(e) => setSalesId(e.target.value)}
            >
              <option value="">
                {translate("crm.reassign.advisor_placeholder")}
              </option>
              {advisors.map((sale) => (
                <option key={sale.id} value={String(sale.id)}>
                  {`${sale.first_name} ${sale.last_name}`}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reassign-reason">
              {translate("crm.reassign.reason_label")}
            </Label>
            <Textarea
              id="reassign-reason"
              value={reason}
              aria-invalid={touched && reasonMissing}
              onChange={(e) => setReason(e.target.value)}
            />
            {touched && reasonMissing ? (
              <p role="alert" className="text-sm text-destructive">
                {translate("crm.reassign.reason_required")}
              </p>
            ) : null}
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={onClose}
            >
              {translate("crm.reassign.cancel")}
            </Button>
            <Button type="submit" className="h-11 sm:h-9" disabled={!canSubmit}>
              {translate("crm.reassign.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
